import { analytics } from '../config/firebase';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';

// Analytics event names
export const ANALYTICS_EVENTS = {
  // Page Views
  PAGE_VIEW: 'page_view',
  PAGE_LOAD: 'page_load',
  
  // Authentication
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  USER_SIGNUP: 'user_signup',
  LOGIN_METHOD: 'login_method',
  
  // Organization Management
  ORG_CREATED: 'organization_created',
  ORG_SELECTED: 'organization_selected',
  ORG_UPDATED: 'organization_updated',
  ORG_DELETED: 'organization_deleted',
  MEMBER_ADDED: 'member_added',
  MEMBER_REMOVED: 'member_removed',
  
  // Menu Management
  MENU_ITEM_CREATED: 'menu_item_created',
  MENU_ITEM_UPDATED: 'menu_item_updated',
  MENU_ITEM_DELETED: 'menu_item_deleted',
  MENU_CATEGORY_CREATED: 'menu_category_created',
  MENU_CATEGORY_UPDATED: 'menu_category_updated',
  MENU_CATEGORY_DELETED: 'menu_category_deleted',
  
  // Order Management
  ORDER_CREATED: 'order_created',
  ORDER_UPDATED: 'order_updated',
  ORDER_DELETED: 'order_deleted',
  ORDER_STATUS_CHANGED: 'order_status_changed',
  PUBLIC_ORDER_CREATED: 'public_order_created',
  
  // Table Management
  TABLE_CREATED: 'table_created',
  TABLE_UPDATED: 'table_updated',
  TABLE_DELETED: 'table_deleted',
  
  // User Actions
  BUTTON_CLICK: 'button_click',
  FORM_SUBMITTED: 'form_submitted',
  ERROR_OCCURRED: 'error_occurred',
  
  // Feature Usage
  FEATURE_USED: 'feature_used',
  DEMO_MODE_ACTIVATED: 'demo_mode_activated',
  
  // Performance
  API_CALL: 'api_call',
  API_ERROR: 'api_error',
  LOAD_TIME: 'load_time',
} as const;

// Analytics service class
class AnalyticsService {
  private isEnabled: boolean = true;

  // Enable/disable analytics
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  // Check if analytics is enabled
  isAnalyticsEnabled(): boolean {
    return this.isEnabled && analytics !== null;
  }

  // Log a custom event
  logEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isAnalyticsEnabled()) return;

    try {
      logEvent(analytics, eventName, parameters);
    } catch (error) {
      // Analytics errors are non-critical, silently ignore
    }
  }

  // Set user ID for tracking
  setUserId(userId: string | null): void {
    if (!this.isAnalyticsEnabled() || !userId) return;

    try {
      setUserId(analytics, userId);
    } catch (error) {
      // Analytics errors are non-critical, silently ignore
    }
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isAnalyticsEnabled()) return;

    try {
      setUserProperties(analytics, properties);
    } catch (error) {
      // Analytics errors are non-critical, silently ignore
    }
  }

  // Track page view
  trackPageView(pageName: string, pagePath?: string): void {
    this.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_name: pageName,
      page_path: pagePath || window.location.pathname,
      page_title: document.title,
    });
  }

  // Track page load time
  trackPageLoad(loadTime: number): void {
    this.logEvent(ANALYTICS_EVENTS.LOAD_TIME, {
      load_time_ms: loadTime,
      page_path: window.location.pathname,
    });
  }

  // Track user login
  trackUserLogin(method: string, userId?: string): void {
    this.logEvent(ANALYTICS_EVENTS.USER_LOGIN, {
      login_method: method,
      user_id: userId,
    });
    
    if (userId) {
      this.setUserId(userId);
    }
  }

  // Track user logout
  trackUserLogout(): void {
    this.logEvent(ANALYTICS_EVENTS.USER_LOGOUT);
    this.setUserId(null);
  }

  // Track organization events
  trackOrganizationCreated(orgName: string, orgUrl: string): void {
    this.logEvent(ANALYTICS_EVENTS.ORG_CREATED, {
      organization_name: orgName,
      organization_url: orgUrl,
    });
  }

  trackOrganizationSelected(orgName: string, orgUrl: string): void {
    this.logEvent(ANALYTICS_EVENTS.ORG_SELECTED, {
      organization_name: orgName,
      organization_url: orgUrl,
    });
  }

  // Track menu events
  trackMenuItemCreated(itemName: string, category: string): void {
    this.logEvent(ANALYTICS_EVENTS.MENU_ITEM_CREATED, {
      item_name: itemName,
      category: category,
    });
  }

  trackMenuItemUpdated(itemName: string, category: string): void {
    this.logEvent(ANALYTICS_EVENTS.MENU_ITEM_UPDATED, {
      item_name: itemName,
      category: category,
    });
  }

  trackMenuItemDeleted(itemName: string, category: string): void {
    this.logEvent(ANALYTICS_EVENTS.MENU_ITEM_DELETED, {
      item_name: itemName,
      category: category,
    });
  }

  // Track order events
  trackOrderCreated(orderId: string, total: number, itemCount: number): void {
    this.logEvent(ANALYTICS_EVENTS.ORDER_CREATED, {
      order_id: orderId,
      order_total: total,
      item_count: itemCount,
    });
  }

  trackOrderStatusChanged(orderId: string, oldStatus: string, newStatus: string): void {
    this.logEvent(ANALYTICS_EVENTS.ORDER_STATUS_CHANGED, {
      order_id: orderId,
      old_status: oldStatus,
      new_status: newStatus,
    });
  }

  trackPublicOrderCreated(orderId: string, orgUrl: string, tableId: string): void {
    this.logEvent(ANALYTICS_EVENTS.PUBLIC_ORDER_CREATED, {
      order_id: orderId,
      organization_url: orgUrl,
      table_id: tableId,
    });
  }

  // Track table events
  trackTableCreated(tableName: string): void {
    this.logEvent(ANALYTICS_EVENTS.TABLE_CREATED, {
      table_name: tableName,
    });
  }

  trackTableUpdated(tableName: string): void {
    this.logEvent(ANALYTICS_EVENTS.TABLE_UPDATED, {
      table_name: tableName,
    });
  }

  trackTableDeleted(tableName: string): void {
    this.logEvent(ANALYTICS_EVENTS.TABLE_DELETED, {
      table_name: tableName,
    });
  }

  // Track button clicks
  trackButtonClick(buttonName: string, pageName?: string): void {
    this.logEvent(ANALYTICS_EVENTS.BUTTON_CLICK, {
      button_name: buttonName,
      page_name: pageName || window.location.pathname,
    });
  }

  // Track form submissions
  trackFormSubmitted(formName: string, success: boolean): void {
    this.logEvent(ANALYTICS_EVENTS.FORM_SUBMITTED, {
      form_name: formName,
      success: success,
    });
  }

  // Track errors
  trackError(errorType: string, errorMessage: string, pageName?: string): void {
    this.logEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error_type: errorType,
      error_message: errorMessage,
      page_name: pageName || window.location.pathname,
    });
  }

  // Track API calls
  trackApiCall(endpoint: string, method: string, success: boolean, duration?: number): void {
    this.logEvent(ANALYTICS_EVENTS.API_CALL, {
      endpoint: endpoint,
      method: method,
      success: success,
      duration_ms: duration,
    });
  }

  // Track feature usage
  trackFeatureUsed(featureName: string): void {
    this.logEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature_name: featureName,
    });
  }

  // Track demo mode activation
  trackDemoModeActivated(): void {
    this.logEvent(ANALYTICS_EVENTS.DEMO_MODE_ACTIVATED);
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService(); 